import { BlueprintInstanceType, type Countertop, degrees2radians, getSurroundFinishProfile, type Island, type Kitchen, type Panel } from '@rta/rta-blueprint-library';

import { type CountertopContact, getCountertopContact, getDrawnBBox } from './get-countertop-contact';
import { getJointCountertopGroups } from './is-joint-countertop';

export type CleanEdgeLabel = {
	id: string;
	x: number;
	y: number;
	rotation: number;
	text: string;
};

export type GetCleanEdgeLabelsParams = {
	island: Island;
	kitchen: Kitchen;
};

const CLEAN_EDGE_TEXT = 'CLEAN EDGE';
const EDGE_EPS = 1;
// End labels sit just OUTSIDE the panel edge, clear of the depth dimension line, with a little
// padding — matching the finalized PDF (~3" beyond the edge). Interior (seam) labels stay on the seam.
const END_LABEL_OFFSET = 6;
// Fallback standard overhang when no finish profile resolves (matches the render-overhang-removal-line gate).
const STANDARD_OVERHANG_FALLBACK = 2;
// RenderBreakpoint draws every seam as an axis-vertical line on every island type, so the text follows the cut.
const SEAM_LABEL_ROTATION = -90;
const HALF_TURN = 180;

// Chiseled-edge CONCRETE tops get a "CLEAN EDGE" label down the slab seam instead of a
// dimensioned "X SEAM" label — the seam line still draws, but its measurement text is suppressed.
export const isCleanEdgeCountertop = (countertop: Countertop | null | undefined): boolean => {
	if (!countertop) return false;
	const materialType = countertop.material?.type?.toString()?.toLowerCase() ?? '';
	const materialEdge = countertop.material?.edge?.toString()?.toUpperCase() ?? '';
	return materialType === 'concrete' && materialEdge === 'CHISELED';
};

// The material's default (un-cut) overhang — same gate `RenderOverhangRemovalLine` uses to flag a
// reduced overhang, so "removed" here means the same thing it means everywhere else in the app.
const getStandardOverhang = (island: Island, kitchen: Kitchen): number => {
	const surround = island.surround ?? kitchen.material?.surround ?? null;
	return getSurroundFinishProfile(surround?.material)?.standardOverhang ?? STANDARD_OVERHANG_FALLBACK;
};

// RenderCountertop draws each panel with `rotate(-getOriginAngle())`; a label on that face carries the same angle, folded into [-90, 90) so it never reads upside down.
const getFaceRotation = (panel: Panel): number => {
	const folded = ((-panel.getOriginAngle() % HALF_TURN) + HALF_TURN) % HALF_TURN;
	return folded >= HALF_TURN / 2 ? folded - HALF_TURN : folded;
};

export type CleanEdgeSide = 'left' | 'right' | 'back' | 'back-left' | 'back-right' | 'front';

type CleanEdgeEnd = {
	panel: Panel;
	side: CleanEdgeSide;
	/** Where the neighbouring slab actually meets this one — absent for wall abutments and manual labels. */
	contact?: CountertopContact | null;
};

// `isBackPanel` is true for BOTH corner diagonals, so they split on topology: back-right is the one whose left neighbour is the right panel.
const getBackSide = (panel: Panel, isCorner: boolean): CleanEdgeSide => {
	if (!isCorner) return 'back';
	return panel.getLeftPanel()?.isRightPanel ? 'back-right' : 'back-left';
};

const getEndSide = (panel: Panel, isCorner: boolean): CleanEdgeSide | null => {
	if (panel.isLeftPanel) return 'left';
	if (panel.isRightPanel) return 'right';
	if (panel.isBackPanel) return getBackSide(panel, isCorner);
	if (panel.isFrontPanel) return 'front';
	return null;
};

// The face that actually looks at the touching slab — geometry locates the junction, not whichever panel a connection record names.
const getContactPanel = (island: Island, contact: CountertopContact): Panel | null => {
	const target = { x: contact.other.cx, y: contact.other.cy };
	const scored = island.panels.children
		.filter((panel) => getEndSide(panel, island.isCornerIsland) !== null)
		.map((panel) => {
			const outward = degrees2radians(panel.getOriginAngle());
			const dx = target.x - panel.center.x;
			const dy = target.y - panel.center.z;
			const distance = Math.hypot(dx, dy) || 1;
			return { panel, alignment: (((dx / distance) * Math.sin(outward)) + ((dy / distance) * Math.cos(outward))) };
		});
	if (scored.length === 0) return null;
	return scored.reduce((best, candidate) => (candidate.alignment > best.alignment ? candidate : best)).panel;
};

// A cut end: a neighbouring top physically TOUCHES this one, or a wall abutment cut the overhang below standard (#593).
const getAutomaticEnds = (island: Island, kitchen: Kitchen, countertop: Countertop, ownJointGroup: Array<Island> | null): Array<CleanEdgeEnd> => {
	const ends: Array<CleanEdgeEnd> = [];
	const displayed = getDrawnBBox(countertop);

	// The same sweep get-adjoining-countertop-seams.ts runs, so a SEAM and a CLEAN EDGE can never disagree about contact.
	if (displayed) {
		kitchen.design.islands.children.forEach((neighbor) => {
			if (neighbor.id === island.id) return;
			// Same joint-countertop group: one shared slab, so no clean edge at the internal joining edge.
			if (ownJointGroup?.some((member) => member.id === neighbor.id)) return;
			const contact = getCountertopContact({ island, neighbor, kitchen, displayed });
			if (!contact) return;
			const panel = getContactPanel(island, contact);
			const side = panel ? getEndSide(panel, island.isCornerIsland) : null;
			if (!panel || !side || ends.some((end) => end.side === side)) return;
			ends.push({ panel, side, contact });
		});
	}

	// An obstruction carries no countertop, so geometry cannot see a wall — the connection record is the only evidence.
	const standardOverhang = getStandardOverhang(island, kitchen);
	[...island.connections.getSourceConnections(), ...island.connections.getTargetConnections()].forEach((connection) => {
		const isSource = connection.sourceIsland?.id === island.id;
		const panel = isSource ? connection.sourcePanel : connection.targetPanel;
		const neighbour = isSource ? connection.targetIsland : connection.sourceIsland;
		if (!panel || neighbour?.instanceOf !== BlueprintInstanceType.Obstruction) return;
		if (panel.countertopOverhang >= standardOverhang) return;
		const side = getEndSide(panel, island.isCornerIsland);
		if (!side || ends.some((end) => end.side === side)) return;
		ends.push({ panel, side, contact: null });
	});

	return ends;
};

type CleanEdgeFrame = {
	centerY: number;
	half: number;
	halfDepth: number;
	isCorner: boolean;
};

// Every nameable side of the island, regardless of what (if anything) it abuts — the manual
// "add a CLEAN EDGE label here" affordance (#398) is not gated by the automatic cut/abutment rule.
const getAllEndCandidates = (island: Island): Array<CleanEdgeEnd> => {
	const candidates: Array<CleanEdgeEnd> = [];
	island.panels.children.forEach((panel) => {
		const side = getEndSide(panel, island.isCornerIsland);
		if (!side || candidates.some((candidate) => candidate.side === side)) return;
		candidates.push({ panel, side });
	});
	return candidates;
};

// Straight islands anchor against the slab's bounding edge; a corner island's faces are all diagonal, so those step out from the panel centre along the face's own outward normal.
const getEndPosition = (end: CleanEdgeEnd, frame: CleanEdgeFrame): { x: number; y: number } => {
	const { panel, side, contact } = end;
	const { centerY, half, halfDepth, isCorner } = frame;
	if (isCorner) {
		const outward = degrees2radians(panel.getOriginAngle());
		const distance = (panel.thickness / 2) + panel.countertopOverhang + END_LABEL_OFFSET;
		return { x: panel.center.x + (Math.sin(outward) * distance), y: panel.center.z + (Math.cos(outward) * distance) };
	}
	// The label marks where the slabs actually meet, so an offset neighbour pulls it off the midpoint (#593).
	const contactAxis = side === 'left' || side === 'right' ? 'x' : 'y';
	const contactCenter = contact && contact.axis === contactAxis ? (contact.start + contact.end) / 2 : null;
	if (side === 'left') return { x: -half - END_LABEL_OFFSET, y: contactCenter ?? centerY };
	if (side === 'right') return { x: half + END_LABEL_OFFSET, y: contactCenter ?? centerY };
	if (side === 'front') return { x: contactCenter ?? 0, y: centerY + halfDepth + END_LABEL_OFFSET };
	return { x: contactCenter ?? 0, y: centerY - halfDepth - END_LABEL_OFFSET };
};

// Center-origin frame matching RenderBreakpointsCountertop (x: ±width/2 = left/right edges, y: centerY ± height/2 = back/front edges)
const getCleanEdgeFrame = (island: Island, countertop: Countertop): CleanEdgeFrame => ({
	half: (countertop.size?.width ?? 0) / 2,
	// countertop.size.height is the front-to-back (depth) extent in this top-view frame — see
	// RenderBreakpointsCountertop, which spans a seam line by this same amount around centerY.
	halfDepth: (countertop.size?.height ?? 0) / 2,
	centerY: countertop.center?.z ?? 0,
	isCorner: island.isCornerIsland
});

// Storage key for a manually-added CLEAN EDGE end label (#398) -- same kitchen.blueprintOverrides
// bag the drag/delete overrides already use, additive key, never touches their shape.
export const getCleanEdgeManualOverrideKey = (islandId: string, side: CleanEdgeSide): string => `cleanEdge.manual.${islandId}.${side}`;

// Ephemeral, render-only label positions (never mutates the domain model): automatic labels for a chiseled-edge concrete top, plus any manually-flagged ends (#398).
export const getCleanEdgeLabels = (params: GetCleanEdgeLabelsParams): Array<CleanEdgeLabel> => {
	const { island, kitchen } = params;

	const [countertop] = kitchen.design.countertops.filterByIslandId(island.id);
	if (!countertop) return [];

	const frame = getCleanEdgeFrame(island, countertop);
	const labels: Array<CleanEdgeLabel> = [];

	// Automatic detection is unchanged; the manual-override loop below is additive and never alters what this block produces (#398)
	if (isCleanEdgeCountertop(countertop)) {
		const ownJointGroup = getJointCountertopGroups(kitchen).find((group) => group.some((member) => member.id === island.id)) ?? null;

		// Interior slab seams — "CLEAN EDGE" down each stored breakpoint (matches the drawn seams).
		const seams = countertop.breakpoints.children;
		seams.forEach((seam, i) => {
			if (Math.abs(Math.abs(seam.center) - frame.half) < EDGE_EPS) return;
			labels.push({ id: `${island.id}.seam.${i}`, x: seam.center, y: frame.centerY, rotation: SEAM_LABEL_ROTATION, text: CLEAN_EDGE_TEXT });
		});

		// Cut end faces where the island butts a neighbour (corner-trim gap) or a wall — pushed just
		// outside the panel edge and its depth dimension so the label reads in the outer margin.
		getAutomaticEnds(island, kitchen, countertop, ownJointGroup).forEach((end) => {
			const { x, y } = getEndPosition(end, frame);
			labels.push({ id: `${island.id}.end.${end.side}`, x, y, rotation: getFaceRotation(end.panel), text: CLEAN_EDGE_TEXT });
		});
	}

	// Manually-added end labels (#398) -- the user can flag any side on demand, independent of the
	// automatic material/cut-overhang rule, e.g. a wall abutment whose overhang wasn't cut.
	getAllEndCandidates(island).forEach((end) => {
		const id = `${island.id}.end.${end.side}`;
		if (labels.some((label) => label.id === id)) return;
		if (kitchen.blueprintOverrides[getCleanEdgeManualOverrideKey(island.id, end.side)] !== '1') return;
		const { x, y } = getEndPosition(end, frame);
		labels.push({ id, x, y, rotation: getFaceRotation(end.panel), text: CLEAN_EDGE_TEXT });
	});

	return labels;
};

export type CleanEdgeAddableCandidate = {
	id: string;
	side: CleanEdgeSide;
	x: number;
	y: number;
	rotation: number;
};

/**
 * Sides that could take a manually-added CLEAN EDGE label right now but don't have one yet
 * (neither automatic nor manual) -- drives the "add CLEAN EDGE here" affordance (#398).
 */
export const getCleanEdgeAddableCandidates = (params: GetCleanEdgeLabelsParams): Array<CleanEdgeAddableCandidate> => {
	const { island, kitchen } = params;

	const [countertop] = kitchen.design.countertops.filterByIslandId(island.id);
	if (!countertop) return [];

	const frame = getCleanEdgeFrame(island, countertop);
	const activeIds = new Set(getCleanEdgeLabels(params).map((label) => label.id));

	return getAllEndCandidates(island)
		.filter((end) => !activeIds.has(`${island.id}.end.${end.side}`))
		.map((end) => {
			const { x, y } = getEndPosition(end, frame);
			return { id: `${island.id}.end.${end.side}`, side: end.side, x, y, rotation: getFaceRotation(end.panel) };
		});
};
